import { ParticipantView, QuestionView } from '@views';
import { useSaveSurveyResponsesAction } from '@actions';
import { useEffect, useState } from 'react';
import { useNextSurveySectionViewCreator } from '@app/view-creator/next-survey-section-view-creator';
import { EmptyFunction } from '@utils/common-types';
import { useAllCohortUsersViewCreator } from '@viewCreators/active-participants-view-creator';
import { useNavigationEligibility } from '@components/SurveyDrawer/NavigationEligibilityContextProvider';
import { useAppCookies } from '@app/providers/AppCookieProvider';

export type SurveyDrawerController = {
  surveyTitle: string;
  isDrawerOpen: boolean;
  activeStep: number;
  currentQuestion: QuestionView;
  questions: QuestionView[];
  handleNext: EmptyFunction;
  handlePrev: EmptyFunction;
  saveResponses: () => void;
  refresh: () => Promise<any>;
  activeParticipants: ParticipantView[];
};

export const useSurveyDrawerController = (groupId: number): SurveyDrawerController => {
  const { data, refetch } = useNextSurveySectionViewCreator(groupId);
  const [activeStep, setActiveStep] = useState<number>(0);
  const { mutateAsync: saveSurveyAction } = useSaveSurveyResponsesAction();
  const [currentQuestion, setCurrentQuestion] = useState<QuestionView>(null as unknown as QuestionView);
  const [questions, setQuestions] = useState<QuestionView[]>([]);
  const [surveyTitle, setSurveyTitle] = useState<string>('');
  const { data: userData } = useAllCohortUsersViewCreator(groupId);
  const { setSectionRef, setCurrentStep, triggerEligibility } = useNavigationEligibility();
  const environment = import.meta.env.VITE_APP_ENV || 'development';
  const cookies = useAppCookies();
  const currentUserId = cookies.profile.id;
  const currentUser = userData?.allActiveCohortUsers.find((p) => p.CohortUser.userId === currentUserId);

  useEffect(() => {
    if (!data) return;
    const { section } = data;
    if (section) {
      setSectionRef(section);
      setSurveyTitle(section.title);

      const allQuestions = section?.questions ?? [];
      setQuestions(allQuestions);

      const step = Math.min(activeStep, allQuestions.length - 1);
      setCurrentQuestion(allQuestions[step]);
      triggerEligibility();
    }
  }, [data, activeStep]);

  // Show the drawer if:
  //   The survey has at least one section
  //   That section has questions
  let isDrawerOpen = !!data && data.section && data.section.questions.length > 0;

  // // If the user is a coach, close the drawer (no survey)
  if (currentUser?.CohortUser.role === 'Coach') {
    isDrawerOpen = false;
  }

  // If the user is a captain or admin, and we're *not* on a development environment, close the drawer (no survey)
  if (
    (currentUser?.CohortUser.role === 'Captain' || currentUser?.CohortUser.user.role === 'Admin') &&
    !['development', 'dev', 'test', 'local', 'staging'].includes(environment)
  ) {
    isDrawerOpen = false;
  }

  const handleNext = () => {
    setActiveStep((prev) => {
      const next = prev + 1;
      setCurrentStep(next);
      return next;
    });
  };

  const handlePrev = () => {
    setActiveStep((prev) => {
      const next = prev - 1;
      setCurrentStep(next);
      return next;
    });
  };

  const handleSaveResponses = async () => {
    if (data) {
      const { section, surveyId } = data;
      await saveSurveyAction({ section, groupId, surveyId: surveyId });
    }
    setActiveStep(0);
    await refetch();
  };
  const activeParticipants = userData?.activeParticipants ?? [];
  return {
    isDrawerOpen,
    questions,
    activeStep,
    surveyTitle,
    currentQuestion,
    handleNext,
    handlePrev,
    saveResponses: handleSaveResponses,
    refresh: refetch,
    activeParticipants,
  };
};
